import { useTheme } from "@mui/material/styles";
import React, { useState, useEffect, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import PersonalDocument from "./EmployeePersonalDocument/EmployeePersonalDocument";
import { State } from "./interfaces";
import { loadEmployeePersonalDocuments } from "../../../services/employees/employee-documents-api.service";

import { EmployeeDataContext } from "../EmployeePersonalAndChangingDocuments";

interface Props {
  updateParentLoading: (isLoading: boolean) => void;
  shouldReload?: boolean;
}
const EmployeePersonalDocuments = ({ updateParentLoading, shouldReload }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP -- translation root path
  const theme = useTheme();
  const { employee, forManager } = useContext(EmployeeDataContext);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    closePopover: false,
  });

  const setClosePopover = (value: boolean): void => setState((state) => ({ ...state, closePopover: value }));

  const getPersonalDocuments = useCallback(
    async (employeeId: string, signal?: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));
      updateParentLoading(true);

      try {
        const { items } = await loadEmployeePersonalDocuments(employeeId, forManager, 0, 100, signal);

        if (!signal || !signal.aborted) setState((state) => ({ ...state, items }));
      } catch (error) {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, error }));
      } finally {
        if (!signal || !signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
          updateParentLoading(false);
        }
      }
    },
    [shouldReload]
  );

  const handleNotify = async () => {
    setClosePopover(true);
    // close pop-over then notify the parent page
    await getPersonalDocuments(employee?.id ?? "");
    setClosePopover(false);
  };

  useEffect(() => {
    const abortController = new AbortController();

    if (employee?.id) getPersonalDocuments(employee?.id ?? "", abortController.signal);

    return () => abortController.abort();
  }, [employee?.id, getPersonalDocuments]);

  useEffect(() => {
    const abortController = new AbortController();

    if (shouldReload && employee?.id) getPersonalDocuments(employee?.id ?? "", abortController.signal);

    return () => abortController.abort();
  }, [shouldReload, employee?.id, getPersonalDocuments]);

  return (
    <>
      {state?.items.map(({ documentType, document }, index) => (
        <PersonalDocument
          key={index}
          document={document}
          documentType={documentType}
          handleNotify={handleNotify}
        />
      ))}
    </>
  );
};

export default EmployeePersonalDocuments;
